import type {
  ButtonNavItem,
  LanguageNavItem,
  PageNavItem,
  ThemeNavItem,
  UrlNavItem,
} from '@unifyapps/defs/types/interface';
import useEventCallback from '@unifyapps/hooks/useEventCallback';
import { type TFunction, useTranslation } from '@unifyapps/i18n/client';
import Logout01 from '@unifyapps/icons/outline/LogOut01';
import { LOGOUT_URL } from '@unifyapps/network/auth/const';
import { Avatar, type AvatarProps } from '@unifyapps/ui/components/Avatar';
import { Divider } from '@unifyapps/ui/components/Divider';
import { MenuList } from '@unifyapps/ui/components/Menu';
import { useKeyboardNavigation } from '@unifyapps/ui/components/Menu/hooks/useKeyboardNavigation';
import type { MenuListItem } from '@unifyapps/ui/components/Menu/types';
import { Stack } from '@unifyapps/ui/components/Stack';
import { Typography } from '@unifyapps/ui/components/Typography';
import { clsx } from 'clsx';
import { Suspense, useMemo } from 'react';
import { EMPTY_OBJECT } from '../consts/empty';
import { useUserMenuNavItems } from '../hooks/useApplicationUserMenuNavItems';
import { useUserMenuAction } from '../hooks/useUserMenuActions';
import { ApplicationMenuList } from './ApplicationMenuList';

const DARK_MODE_STYLE = {
  '--avatar-contrast-border': 'var(--utility-brand-500)',
} as React.CSSProperties;

const DARK_MODE_SLOT_PROPS: AvatarProps['slotProps'] = {
  root: { style: DARK_MODE_STYLE },
};

export type InterfaceUserMenuProps = {
  name?: string;
  email?: string;
  className?: string;
  disabled?: boolean;
  onProfileClick?: () => void;
  menuItems?: (UrlNavItem | ThemeNavItem | LanguageNavItem | ButtonNavItem | PageNavItem)[];
  onClose?: () => void;
  activePathnames?: string[];
  scheme?: 'light' | 'dark';
};

const getMenuItems = ({ disabled, t }: { disabled?: boolean; t: TFunction }) => [
  {
    type: 'label' as const,
    id: 'logout',
    title: t('common:Logout'),
    startDecoratorNode: (
      <Logout01
        className={clsx('', {
          'text-icon-disabled': disabled,
        })}
        height={20}
        stroke="var(--text-tertiary)"
        strokeWidth={1.5}
        width={20}
      />
    ),
    url: LOGOUT_URL,
    className: '!py-md ps-sm pe-sm !m-0 !bg-transparent hover:!bg-secondary_hover',
    disabled,
    titleClassName: clsx('font-semibold text-secondary', {
      'text-disabled': disabled,
    }),
  },
];

export function ApplicationUserMenu({
  name,
  email,
  className,
  disabled,
  onProfileClick,
  menuItems: userMenuNavItems,
  onClose,
  activePathnames,
  scheme,
}: InterfaceUserMenuProps) {
  const { t } = useTranslation(['common']);
  const { onAction } = useUserMenuAction({ userMenuNavItems, onClose });

  const menuListOnAction = useEventCallback((menuItemId: string) => {
    void onAction(menuItemId);
  });

  const menuItems = useMemo(() => getMenuItems({ disabled, t }), [disabled, t]);

  const { userMenuNavMenuItems, localeOptions, hasNavSection } = useUserMenuNavItems({
    userMenuNavItems,
    activePathnames,
  });

  const { activeIndex, onMouseEnter } = useKeyboardNavigation({
    enableKeyboardNavigation: true,
    items: (userMenuNavMenuItems ?? []) as MenuListItem[],
    onAction,
  });

  const itemClassName = (index: number, item: MenuListItem) =>
    clsx(item.className, {
      'bg-secondary_hover': index === activeIndex,
    });

  return (
    <Stack
      className={clsx('w-full rounded-3xl border-none bg-primary', className)}
      data-color-scheme={scheme}
      direction="column"
    >
      <Stack
        alignItems="center"
        className={clsx('bg-primary gap-x-lg p-lg', {
          'hover:bg-secondary cursor-pointer': Boolean(onProfileClick),
        })}
        direction="row"
        onClick={onProfileClick}
      >
        {name ? (
          <Avatar
            className={scheme === 'dark' ? '!bg-utility-brand-25 !text-utility-brand-700' : ''}
            color="brand_subtle"
            name={name}
            size="sm"
            slotProps={
              scheme === 'dark' ? DARK_MODE_SLOT_PROPS : (EMPTY_OBJECT as AvatarProps['slotProps'])
            }
          />
        ) : null}
        <Stack direction="column">
          {name || email ? (
            <Stack direction="column">
              {name ? (
                <Typography
                  className="w-full"
                  color="text-secondary"
                  maxNumberOfLines={1}
                  variant="text-sm"
                  weight="semi-bold"
                >
                  {name}
                </Typography>
              ) : null}
              {email ? (
                <Typography
                  className="text-tertiary w-full"
                  maxNumberOfLines={3}
                  variant="text-xs"
                  weight="regular"
                >
                  {email}
                </Typography>
              ) : null}
            </Stack>
          ) : null}
        </Stack>
      </Stack>
      {hasNavSection ? (
        <>
          <Divider className="mx-lg my-xxs" />
          {userMenuNavMenuItems && userMenuNavMenuItems?.length > 0 ? (
            // The theme/language switcher rows (and the language action hook) call useTranslation
            // for lazily-loaded runtime namespaces (blocks, interfaces-matrix) on first open. The
            // menu mounts inside a popover whose nearest Suspense boundary sits above the side nav,
            // so without this local boundary that first-open fetch unwinds the ancestor and blanks
            // the whole nav for a frame. Catch it here so only the menu waits, never the nav.
            <Suspense fallback={null}>
              <ApplicationMenuList
                itemClassName={itemClassName}
                items={userMenuNavMenuItems}
                localeOptions={localeOptions}
                onAction={menuListOnAction}
                onMouseEnter={onMouseEnter}
              />
            </Suspense>
          ) : null}
        </>
      ) : null}

      <Divider className="mx-lg my-xxs" />
      <MenuList
        className={clsx('rounded-3xl', {
          '!p-lg': !hasNavSection,
          '!px-lg !py-sm': hasNavSection,
        })}
        items={menuItems}
        menuContentContainerClassName="gap-x-md"
        onAction={menuListOnAction}
      />
    </Stack>
  );
}
